import { FileText } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { DemoChatMessage } from '../model/message';

interface Props {
  message: DemoChatMessage;
}

const formatTime = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const isImageUrl = (url: string): boolean => /\.(jpe?g|png|gif|webp|bmp|heic|heif)(\?|$)/i.test(url);

const MessageItem = ({ message }: Props) => {
  const isClientMessage = message.role === 'user' || message.sender === 'client';
  const isAssistantSystemNone = message.sender === 'none';

  const bubbleClasses = cn(
    'max-w-[80%] rounded-lg p-2 text-base leading-5 whitespace-pre-wrap break-words',
    isClientMessage && 'bg-brand-vivid text-text-inverse',
    !isClientMessage && isAssistantSystemNone && 'bg-brand-turquoise text-brand-navy',
    !isClientMessage && !isAssistantSystemNone && 'bg-bg-surface-alt text-text-default'
  );

  const fileUrl = message.fileUrl ?? null;
  const hasFile = Boolean(fileUrl);
  const showAsImage = hasFile && fileUrl !== null && isImageUrl(fileUrl);

  const useLightBubbleLinks = isClientMessage || isAssistantSystemNone;

  const linkClasses = useLightBubbleLinks
    ? 'text-inherit underline-offset-2 hover:underline'
    : 'text-brand-vivid underline-offset-2 hover:underline';

  return (
    <div className={cn('flex w-full flex-col gap-1', isClientMessage ? 'items-end' : 'items-start')}>
      <div className={bubbleClasses}>
        {hasFile && (
          <div className="mb-2">
            {showAsImage ? (
              <a href={fileUrl ?? '#'} target="_blank" rel="noreferrer">
                <img
                  src={fileUrl ?? ''}
                  alt={message.fileName ?? 'attachment'}
                  className={cn(
                    'max-h-64 rounded-md object-cover',
                    isClientMessage && 'border border-white/25',
                    !isClientMessage && !isAssistantSystemNone && 'border border-border-subtle'
                  )}
                />
              </a>
            ) : (
              <a
                href={fileUrl ?? '#'}
                target="_blank"
                rel="noreferrer"
                className={cn('flex items-center gap-2 text-base leading-5', linkClasses)}
              >
                <FileText className="size-4 shrink-0" />
                {message.fileName ?? 'archivo'}
              </a>
            )}
          </div>
        )}
        {message.text && <p className="m-0 px-1">{message.text}</p>}
      </div>
      <span className={cn('px-1 text-sm leading-5 text-text-subtle', isClientMessage ? 'self-end' : 'self-start')}>
        {formatTime(message.createdAt)}
      </span>
    </div>
  );
};

export default MessageItem;
